import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';
import { thumbUrl, absoluteImageUrl } from '@/lib/imageUrl';
import { toLocalDateISO } from '@/lib/formatEventDate';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, MessageSquare, Loader2, X, GraduationCap, Clock, Calendar, Users, ExternalLink, Zap } from 'lucide-react';
import { PremiumInstructorBadge } from '@/components/PremiumInstructorBadge';

const SKILL_LEVELS = ['newbie','beginner','intermediate','advanced'];
const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const TIMES = ['morning','afternoon','evening','flexible'];

function PlayerDetailModal({ player, onClose, onMessage }) {
  if (!player) return null;
  // Modal uses the full-size image (visible detail view)
  const photoUrl = absoluteImageUrl(player.profile_photo);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose} data-testid="player-detail-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Player Profile</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="close-player-modal"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-5">
          {/* Header */}
          <div className="flex items-center gap-4">
            {photoUrl ? (
              <img src={photoUrl} alt={player.display_name} className="w-16 h-16 rounded-full object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[#56dfb4]/15 flex items-center justify-center text-2xl font-bold text-[#0f172a]">
                {(player.display_name || 'U')[0].toUpperCase()}
              </div>
            )}
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{player.display_name}</h3>
                {player.is_premium_instructor && <PremiumInstructorBadge size="sm" />}
              </div>
              <div className="flex items-center gap-1 text-sm text-slate-500">
                <MapPin className="w-3.5 h-3.5" />
                {player.distance != null ? `~${player.distance} mi away` : player.zip_code || 'Location not set'}
              </div>
            </div>
          </div>

          {/* Bio */}
          {player.bio && (
            <div>
              <Label className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1 block">About</Label>
              <p className="text-sm text-slate-600">{player.bio}</p>
            </div>
          )}

          {/* Mahjong Profile */}
          <div>
            <Label className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-2 block">Mahjong Profile</Label>
            <div className="flex flex-wrap gap-1.5">
              <Badge variant="secondary" className="text-xs capitalize rounded-full">{player.skill_level}</Badge>
              {player.has_mahjong_set === 'yes' && <Badge className="text-xs bg-[#56dfb4]/15 text-[#0f172a] rounded-full">Has Mahjong Set</Badge>}
              {player.meetup_type === 'public' ? <Badge variant="outline" className="text-xs rounded-full">Public Meetups Only</Badge> : <Badge variant="outline" className="text-xs rounded-full">Public & Private</Badge>}
              {player.gender_preference && player.gender_preference !== 'no_preference' && <Badge variant="outline" className="text-xs capitalize rounded-full">{player.gender_preference.replace(/_/g,' ')}</Badge>}
            </div>
          </div>

          {/* Availability */}
          <div>
            <Label className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-2 block">Availability</Label>
            {player.day_times && Object.keys(player.day_times).some(d => player.day_times[d]?.length) ? (
              <div className="space-y-1.5">
                {(player.days_available || []).map(d => (
                  <div key={d} className="flex items-center gap-2 text-xs">
                    <span className="font-medium capitalize w-20">{d.slice(0,3)}</span>
                    <div className="flex gap-1">{(player.day_times[d] || []).map(t => <Badge key={t} variant="outline" className="text-xs rounded-full capitalize">{t}</Badge>)}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-start gap-2">
                  <Calendar className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-xs text-slate-600">Days</p>
                    <p className="text-xs text-slate-500">{(player.days_available||[]).map(d=>d.charAt(0).toUpperCase()+d.slice(1,3)).join(', ') || 'Not set'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-xs text-slate-600">Times</p>
                    <p className="text-xs text-slate-500 capitalize">{(player.time_preferences||[]).join(', ') || 'Not set'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Instructor Profile */}
          {player.is_instructor && (
            <div>
              <Label className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-2 block">Instructor Profile</Label>
              <div className="p-3 rounded-xl bg-[#b456df]/5 border border-[#b456df]/20">
                <div className="flex items-center gap-2 mb-1">
                  <GraduationCap className="w-4 h-4 text-[#b456df]" />
                  <span className="text-sm font-medium">Mahjong Instructor</span>
                </div>
                {player.instructor_bio && <p className="text-xs text-slate-600 mt-1">{player.instructor_bio}</p>}
                {player.instructor_rate && <p className="text-xs text-slate-500 mt-1">Rate: {player.instructor_rate}</p>}
                {(player.instructor_lesson_types||[]).length > 0 && (
                  <div className="flex gap-1 mt-2">{player.instructor_lesson_types.map(t=><Badge key={t} className="text-xs bg-[#b456df]/10 text-[#b456df] rounded-full capitalize">{t}</Badge>)}</div>
                )}
              </div>
            </div>
          )}

          {/* Social Media */}
          {(player.social_facebook || player.social_instagram || player.social_twitter || player.social_linkedin) && (
            <div>
              <Label className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-2 block">Social Media</Label>
              <div className="space-y-1.5">
                {player.social_facebook && <a href={player.social_facebook.startsWith('http') ? player.social_facebook : `https://${player.social_facebook}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-[#56c6df] hover:underline"><ExternalLink className="w-3 h-3" />Facebook</a>}
                {player.social_instagram && <p className="text-xs text-slate-600">Instagram: <span className="text-[#56c6df]">@{player.social_instagram.replace('@','')}</span></p>}
                {player.social_twitter && <p className="text-xs text-slate-600">X/Twitter: <span className="text-[#56c6df]">@{player.social_twitter.replace('@','')}</span></p>}
                {player.social_linkedin && <a href={player.social_linkedin.startsWith('http') ? player.social_linkedin : `https://${player.social_linkedin}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-[#56c6df] hover:underline"><ExternalLink className="w-3 h-3" />LinkedIn</a>}
              </div>
            </div>
          )}

          {/* Action */}
          <Button onClick={() => { onMessage(player.id); onClose(); }} className="w-full rounded-full bg-[#56c6df] text-white hover:bg-[#3eb0c9] gap-2" data-testid="modal-message-btn">
            <MessageSquare className="w-4 h-4" /> Send Message
          </Button>
        </div>
      </div>
    </div>
  );
}

function GameRequestModal({ onClose, onCreated }) {
  const todayIso = toLocalDateISO();
  const [seats, setSeats] = useState(3);
  const [date, setDate] = useState(todayIso);
  const [time, setTime] = useState('19:00');
  const [where, setWhere] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!date || !time) { toast.error('Pick a date and time'); return; }
    setSubmitting(true);
    try {
      // Convert local date+time to ISO 8601 UTC.
      const dt = new Date(`${date}T${time}`);
      if (isNaN(dt.getTime())) { toast.error('Invalid date or time'); setSubmitting(false); return; }
      const { data } = await api.post('/game-requests', {
        seats_needed: seats,
        game_when: dt.toISOString(),
        where_text: where.trim(),
        message: message.trim(),
      });
      toast.success(`Game request posted! Broadcast to ${data.broadcast_count} player${data.broadcast_count === 1 ? '' : 's'}.`);
      onCreated(data.request);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post request');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose} data-testid="game-request-modal">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Post a Game Request</h2>
            <p className="text-xs text-slate-500 mt-0.5">We'll email opted-in players within 25 miles. First to RSVP gets the seat.</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center flex-shrink-0" data-testid="close-game-request-modal"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <Label className="text-sm font-medium mb-2 block">How many players do you need? *</Label>
            <div className="flex gap-2">
              {[1, 2, 3].map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setSeats(n)}
                  data-testid={`game-request-seats-${n}`}
                  className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-all border ${seats === n ? 'bg-[#df5681] text-white border-[#df5681]' : 'border-slate-200 hover:border-[#df5681]/50'}`}
                >
                  {n} {n === 1 ? 'player' : 'players'}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="gr-date" className="text-sm font-medium mb-1 block">Date *</Label>
              <Input id="gr-date" type="date" value={date} min={todayIso} onChange={e => setDate(e.target.value)} data-testid="game-request-date" />
            </div>
            <div>
              <Label htmlFor="gr-time" className="text-sm font-medium mb-1 block">Time *</Label>
              <Input id="gr-time" type="time" value={time} onChange={e => setTime(e.target.value)} data-testid="game-request-time" />
            </div>
          </div>

          <div>
            <Label htmlFor="gr-where" className="text-sm font-medium mb-1 block">Where (optional)</Label>
            <Input
              id="gr-where"
              type="text"
              placeholder="e.g., My place — DM for address"
              value={where}
              maxLength={200}
              onChange={e => setWhere(e.target.value)}
              data-testid="game-request-where-input"
            />
            <p className="text-xs text-slate-400 mt-1">Tip: keep your home address vague — confirmed players can message you for the exact spot.</p>
          </div>

          <div>
            <Label htmlFor="gr-message" className="text-sm font-medium mb-1 block">Message (optional)</Label>
            <Textarea
              id="gr-message"
              placeholder="e.g., Casual American Mahjong, all skill levels welcome!"
              value={message}
              maxLength={500}
              rows={3}
              onChange={e => setMessage(e.target.value)}
              data-testid="game-request-message-input"
            />
          </div>

          <div className="bg-amber-50 border border-amber-200/60 rounded-xl p-3 flex items-start gap-2">
            <span className="text-amber-500 text-sm mt-0.5">&#9888;</span>
            <p className="text-xs text-amber-700">
              Once you post, the request runs until 30 minutes after game time. Players RSVP first-come, first-served. Overflow goes on a waitlist and gets auto-promoted if a confirmed player drops.
            </p>
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="rounded-full flex-1" data-testid="game-request-cancel-modal-btn">Cancel</Button>
            <Button
              onClick={submit}
              disabled={submitting}
              className="rounded-full flex-1 bg-[#df5681] text-white hover:bg-[#c44570] gap-2"
              data-testid="game-request-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              Post Request
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}


export default function FindPlayersPage() {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [gameRequestModalOpen, setGameRequestModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    skill_levels: [], days_available: [], time_preferences: [],
    has_mahjong_set: 'no_preference', players_needed: 3,
  });
  // Optional zip-code override — lets a searcher find players near a
  // friend, a travel destination, or a planned event location instead
  // of only searching from their own profile's zip. Blank = use their
  // own profile zip (unchanged legacy behavior).
  const [zipCode, setZipCode] = useState('');

  const toggleFilter = (key, value) => {
    setFilters(p => ({...p, [key]: p[key].includes(value) ? p[key].filter(v=>v!==value) : [...p[key], value]}));
  };

  const selectAll = (key, allValues) => {
    setFilters(p => ({...p, [key]: p[key].length === allValues.length ? [] : [...allValues]}));
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const payload = {...filters};
      if (!payload.skill_levels.length) delete payload.skill_levels;
      if (!payload.days_available.length) delete payload.days_available;
      if (!payload.time_preferences.length) delete payload.time_preferences;
      // Only pass zip_code when user actually typed one — empty string
      // makes the backend fall back to profile zip. Trim to first 5.
      const zTrim = zipCode.replace(/\D/g, '').slice(0, 5);
      if (zTrim) payload.zip_code = zTrim;
      const { data } = await api.post('/players/search', payload);
      setPlayers(data.players || []);
      setSearched(true);
    } catch (err) {
      if (err.response?.status === 403) toast.error('Upgrade your plan to search players');
      else toast.error('Search failed');
    } finally { setLoading(false); }
  };

  const sendMessage = (playerId) => {
    navigate(`/messages?to=${playerId}`);
  };

  const renderFilterSection = (label, key, allValues) => (
    <div>
      <Label className="text-sm font-medium mb-1 block">{label}</Label>
      <p className="text-xs text-slate-400 mb-2">Select all that apply</p>
      <label className="flex items-center gap-2 cursor-pointer mb-1.5 pb-1.5 border-b border-slate-100">
        <Checkbox checked={filters[key].length === allValues.length} onCheckedChange={() => selectAll(key, allValues)} data-testid={`select-all-${key}`} />
        <span className="text-sm font-medium text-[#56dfb4]">Select All</span>
      </label>
      <div className="space-y-1.5">
        {allValues.map(v => (
          <label key={v} className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={filters[key].includes(v)} onCheckedChange={() => toggleFilter(key, v)} data-testid={`filter-${key.replace('_','-')}-${v}`} />
            <span className="text-sm capitalize">{v}</span>
          </label>
        ))}
      </div>
    </div>
  );

  return (
    <div data-testid="find-players-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Find Players</h1>
        <p className="text-slate-500 mt-1">Search for compatible Mahjong players in your area</p>
      </div>

      <div className="mb-6 p-3 rounded-xl bg-amber-50 border border-amber-200/50 flex items-start gap-2" data-testid="safety-tip">
        <span className="text-amber-500 text-sm mt-0.5">&#9888;</span>
        <p className="text-xs text-amber-700">
          <strong>Safety tip:</strong> For your first meetup with a new player, we recommend meeting in a public place like a coffee shop, library, or community center. Tell a friend or family member about your plans.
        </p>
      </div>

      {/* Need a Player Now */}
      <div className="mb-6 p-4 rounded-2xl bg-[#df5681]/5 border border-[#df5681]/20 flex flex-col sm:flex-row items-center gap-3" data-testid="last-minute-alert-section">
        <Zap className="w-6 h-6 text-[#df5681] flex-shrink-0" />
        <div className="flex-1 text-center sm:text-left">
          <p className="text-sm font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>Need a player right now?</p>
          <p className="text-xs text-slate-500">Post a request and we'll email opted-in players within 25 miles with a one-tap RSVP link.</p>
        </div>
        <Button
          onClick={() => setGameRequestModalOpen(true)}
          className="rounded-full bg-[#df5681] text-white hover:bg-[#c44570] gap-1.5 px-5 flex-shrink-0"
          data-testid="open-game-request-modal-btn"
        >
          <Zap className="w-4 h-4" /> Post Game Request
        </Button>
      </div>

      {gameRequestModalOpen && (
        <GameRequestModal
          onClose={() => setGameRequestModalOpen(false)}
          onCreated={(req) => { setGameRequestModalOpen(false); navigate(`/game-requests/${req.request_id}`); }}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filters Sidebar */}
        <div className="lg:col-span-1 space-y-4">
          <Card className="border-slate-100">
            <CardContent className="p-5 space-y-5">
              {/* Zip-code override — lets searcher explore other areas
                  (planning travel, helping a friend, scouting events). */}
              <div>
                <Label htmlFor="find-players-zip" className="text-sm font-medium mb-1 block">Search near ZIP</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <Input
                    id="find-players-zip"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={5}
                    placeholder="Defaults to your zip"
                    value={zipCode}
                    onChange={e => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    className="pl-9 rounded-full"
                    data-testid="find-players-zip-input"
                  />
                  {zipCode && (
                    <button
                      type="button"
                      onClick={() => setZipCode('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-slate-100 flex items-center justify-center"
                      aria-label="Clear zip"
                      data-testid="find-players-zip-clear"
                    >
                      <X className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-1">Leave blank to use your profile zip.</p>
              </div>
              {renderFilterSection('Skill Level', 'skill_levels', SKILL_LEVELS)}
              {renderFilterSection('Days Available', 'days_available', DAYS)}
              {renderFilterSection('Time of Day', 'time_preferences', TIMES)}
              <div>
                <Label className="text-sm font-medium mb-2 block">Has Mahjong Set</Label>
                <div className="flex flex-wrap gap-2">
                  {['no_preference','yes','no'].map(v => (
                    <button key={v} type="button" onClick={() => setFilters(p=>({...p, has_mahjong_set: v}))}
                      className={`px-3 py-1.5 rounded-full text-xs border transition-all capitalize ${filters.has_mahjong_set===v?'bg-[#56dfb4] text-slate-900 border-[#56dfb4]':'border-slate-200'}`}>
                      {v === 'no_preference' ? 'Any' : v}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">Players Needed</Label>
                <div className="flex gap-2">
                  {[1,2,3].map(n => (
                    <button key={n} type="button" onClick={() => setFilters(p=>({...p, players_needed: n}))}
                      className={`px-4 py-2 rounded-full text-sm border transition-all ${filters.players_needed===n?'bg-[#56c6df] text-white border-[#56c6df]':'border-slate-200'}`}
                      data-testid={`filter-players-${n}`}>
                      {n}
                    </button>
                  ))}
                </div>
              </div>
              <Button onClick={handleSearch} disabled={loading} className="w-full rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] gap-2" data-testid="search-players-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Search Players
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Results */}
        <div className="lg:col-span-3">
          {!searched ? (
            <div className="text-center py-20 text-slate-400">
              <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">Use the filters to find players</p>
              <p className="text-sm mt-1">Set your preferences and click Search</p>
            </div>
          ) : players.length === 0 ? (
            <div className="text-center py-20 text-slate-400">
              <p className="text-lg font-medium">No players found</p>
              <p className="text-sm mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {players.map(player => {
                const photoUrl = thumbUrl(player.profile_photo);
                return (
                <Card key={player.id} className="border-slate-100 hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer" onClick={() => setSelectedPlayer(player)} data-testid={`player-card-${player.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3 mb-3">
                      {photoUrl ? (
                        <img src={photoUrl} alt={player.display_name} loading="lazy" decoding="async" className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-[#56dfb4]/15 flex items-center justify-center text-lg font-bold">
                          {(player.display_name || 'U')[0].toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <h3 className="font-semibold truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{player.display_name}</h3>
                          {player.is_premium_instructor && <PremiumInstructorBadge size="sm" />}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <MapPin className="w-3 h-3" />
                          {player.distance !== null && player.distance !== undefined ? `~${player.distance} mi away` : player.zip_code || 'Unknown'}
                        </div>
                      </div>
                    </div>
                    {player.bio && <p className="text-xs text-slate-500 mb-2 line-clamp-2">{player.bio}</p>}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      <Badge variant="secondary" className="text-xs capitalize rounded-full">{player.skill_level}</Badge>
                      {player.has_mahjong_set === 'yes' && <Badge className="text-xs bg-[#56dfb4]/15 text-[#0f172a] rounded-full">Has Set</Badge>}
                      {player.meetup_type === 'public' && <Badge variant="outline" className="text-xs rounded-full">Public Only</Badge>}
                      {player.is_instructor && <Badge className="text-xs bg-[#b456df]/10 text-[#b456df] rounded-full">Instructor</Badge>}
                    </div>
                    <div className="text-xs text-slate-500 mb-3">
                      {player.day_times && Object.keys(player.day_times).some(d => player.day_times[d]?.length) ? (
                        <div className="space-y-0.5">
                          {(player.days_available||[]).slice(0,3).map(d => (
                            <p key={d}><span className="capitalize font-medium">{d.slice(0,3)}</span>: {(player.day_times[d]||[]).join(', ') || 'any'}</p>
                          ))}
                          {(player.days_available||[]).length > 3 && <p className="text-slate-400">+{(player.days_available||[]).length - 3} more days</p>}
                        </div>
                      ) : (
                        <>
                          <p>Available: {(player.days_available||[]).slice(0,3).map(d=>d.slice(0,3)).join(', ')}{(player.days_available||[]).length>3?'...':''}</p>
                          <p>Times: {(player.time_preferences||[]).join(', ') || 'Not set'}</p>
                        </>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={(e) => { e.stopPropagation(); sendMessage(player.id); }} size="sm" className="flex-1 rounded-full bg-[#56c6df] text-white hover:bg-[#3eb0c9] gap-1" data-testid={`message-player-${player.id}`}>
                        <MessageSquare className="w-3.5 h-3.5" /> Message
                      </Button>
                      <Button onClick={(e) => { e.stopPropagation(); setSelectedPlayer(player); }} size="sm" variant="outline" className="rounded-full" data-testid={`view-player-${player.id}`}>
                        View
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )})}
            </div>
          )}
        </div>
      </div>

      {/* Player Detail Modal */}
      {selectedPlayer && <PlayerDetailModal player={selectedPlayer} onClose={() => setSelectedPlayer(null)} onMessage={sendMessage} />}
    </div>
  );
}
